import React, { useMemo } from "react";
import { graphql } from "gatsby";
import Section from "@bmi-digital/components/section";
import Container from "@bmi-digital/components/container";
import ColorPair from "@bmi-digital/components/color-pair";
import { useTheme } from "@mui/material/styles";
import type { EmbeddedRegistrationFormSection as EmbeddedRegistrationFormSectionType } from "@bmi/payload-types";
import createImageProps from "../image/createImageProps";
import {
  FormContentWrapper,
  StyledEmbeddedRegistrationFormSection,
  StyledMedia,
  StyledRichText
} from "./styles/EmbeddedRegistrationFormSectionStyles";
import EmbeddedRegistrationForm from "./EmbeddedRegistrationForm";
import type { Color } from "@bmi-digital/components/color-pair";
import type { RichTextData } from "../RichText";
import type { PropsImageVillain } from "../image/types";

export type EmbeddedRegistrationFormSectionData = Pick<
  EmbeddedRegistrationFormSectionType,
  "blockType"
> & {
  embeddedRegistrationFormSectionTitle: string;
  richTextDescription: RichTextData | null;
  backgroundColor: "White" | "Alabaster" | "Blue" | null;
  featuredImage: PropsImageVillain;
  redirectURL: string | null;
};

type Props = {
  data: EmbeddedRegistrationFormSectionData;
};

const BackGroundColorMap: Record<
  Required<EmbeddedRegistrationFormSectionType>["backgroundColor"],
  Color
> = {
  Alabaster: "alabaster",
  White: "white",
  Blue: "blue800"
};

const EmbeddedRegistrationFormSection = ({ data }: Props) => {
  const theme = useTheme();

  const media = useMemo(
    () =>
      createImageProps(
        {
          ...data.featuredImage,
          size: "cover"
        },
        theme
      ),
    [data.featuredImage, theme]
  );

  return (
    <ColorPair
      data-testid="embedded-registration-form-section"
      color={
        data.backgroundColor
          ? BackGroundColorMap[data.backgroundColor]
          : "white"
      }
      markupComponent={StyledEmbeddedRegistrationFormSection}
    >
      <Container>
        <FormContentWrapper>
          <Section.Title data-testid="embedded-registration-form-section-title">
            {data.embeddedRegistrationFormSectionTitle}
          </Section.Title>
          {data.richTextDescription ? (
            <StyledRichText
              document={data.richTextDescription}
              data-testid="embedded-registration-form-section-description"
            />
          ) : null}
          <EmbeddedRegistrationForm
            redirectURL={data.redirectURL}
            backgroundColor={data.backgroundColor}
          />
        </FormContentWrapper>
      </Container>
      <StyledMedia
        {...media}
        data-testid="embedded-registration-form-featured-image"
      />
    </ColorPair>
  );
};

export default EmbeddedRegistrationFormSection;

export const query = graphql`
  fragment EmbeddedRegistrationFormSectionFragment on PayloadEmbeddedRegistrationFormSection {
    blockType
    embeddedRegistrationFormSectionTitle: title
    backgroundColor
    richTextDescription: description {
      ...RichTextFragment
    }
    featuredImage: featuredMedia {
      ...ImageHeroFragment
    }
    redirectURL
  }
`;
